import { Check, Copy, ExternalLink, Link2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useDeleteShare, usePii } from '../../api/queries.ts';
import type { UploadInfo } from '../../api/types.ts';
import { Button } from '../../components/ui/button.tsx';
import { RowList } from '../../components/ui/card.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog.tsx';
import { EmptyNote, ErrorNote, Loading, errorMessage } from '../../components/ui/feedback.tsx';

/** The link split for display: the host quiet, the path that tells links apart in front. */
export function linkParts(url: string): { host: string; path: string } {
  try {
    const parsed = new URL(url);
    const path = `${parsed.pathname}${parsed.search}`.replace(/^\/+/, '');
    return { host: parsed.host, path: path || parsed.host };
  } catch {
    return { host: '', path: url };
  }
}

/** Shared email links the account created, with the chance to take one down. */
export function SharedEmailsSection() {
  const pii = usePii();
  if (pii.isPending) return <Loading label="Loading your shared emails…" />;
  if (pii.isError) return <ErrorNote title="Could not load your shared emails">{errorMessage(pii.error)}</ErrorNote>;
  const uploads = pii.data.uploadsInfo ?? [];
  if (uploads.length === 0) {
    return (
      <EmptyNote icon={<Link2 className="h-5 w-5" />} title="Nothing shared">
        When you share an email as a link from the app, it is listed here so you can take it down later.
      </EmptyNote>
    );
  }
  return <UploadsList uploads={uploads} />;
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };
  return (
    <Button variant="ghost" size="icon" onClick={copy} track="share_copy_link" aria-label={copied ? 'Link copied' : 'Copy link'} title={copied ? 'Copied' : 'Copy link'}>
      {copied ? <Check className="h-4 w-4 text-ok" /> : <Copy className="h-4 w-4" />}
    </Button>
  );
}

function UploadsList({ uploads }: { uploads: UploadInfo[] }) {
  const [pending, setPending] = useState<UploadInfo | null>(null);
  const remove = useDeleteShare();

  const confirm = () => {
    if (!pending) return;
    remove.mutate(pending.url, { onSettled: () => setPending(null) });
  };

  return (
    <>
      {remove.isError ? <ErrorNote title="Could not delete the shared email">{errorMessage(remove.error)}</ErrorNote> : null}
      <RowList>
        {uploads.map((upload) => {
          const { host, path } = linkParts(upload.url);
          return (
            <li key={upload.url} className="flex items-center gap-1 py-3 pr-2 pl-5 sm:gap-3 sm:pr-3 sm:pl-6">
              <span className="hidden h-10 w-10 shrink-0 items-center justify-center sm:flex rounded-2xl bg-brand-soft text-brand">
                <Link2 className="h-[18px] w-[18px]" aria-hidden />
              </span>
              <a href={upload.url} target="_blank" rel="noreferrer" className="group min-w-0 flex-1 rounded-md">
                <span className="flex items-center gap-1.5 truncate font-semibold text-ink group-hover:text-brand">
                  <span className="truncate">{path}</span>
                  <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
                </span>
                {host ? <span className="block truncate text-sm text-muted">{host}</span> : null}
              </a>
              <CopyButton value={upload.url} />
              <Button
                variant="ghost"
                size="icon"
                className="text-danger hover:bg-danger-soft"
                onClick={() => setPending(upload)}
                track="share_delete"
                disabled={remove.isPending}
                aria-label={`Delete ${path}`}
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          );
        })}
      </RowList>

      <Dialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this shared email?</DialogTitle>
            <DialogDescription>The link stops working for everyone who has it. You cannot undo this.</DialogDescription>
          </DialogHeader>
          {pending ? <p className="truncate rounded-xl bg-paper px-4 py-3 text-sm text-ink">{pending.url}</p> : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)} track="share_delete_keep">
              Keep it
            </Button>
            <Button variant="destructive" onClick={confirm} disabled={remove.isPending} track="share_delete_confirm">
              {remove.isPending ? 'Deleting…' : 'Delete link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
